import WalletIcon from '@material-design-icons/svg/filled/account_balance_wallet.svg?react';
import CloudIcon from '@material-design-icons/svg/filled/cloud.svg?react';
import InfoIcon from '@material-design-icons/svg/filled/info.svg?react';
import SecurityIcon from '@material-design-icons/svg/filled/security.svg?react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { usePreferences } from '../../../contexts/PreferencesContext';
import { getErrorMessage } from '../../../utils/errorHandling';
import { captureAndReportError } from '../../../utils/errorReporting';
import {
  clearS3Config,
  decryptS3Config,
  encryptS3Config,
  getS3PublicConfig,
  hasS3Config,
  maskS3Config,
} from '../../../utils/s3CredentialManager';
import { S3Provider, type S3Config } from '../../../utils/upload-providers';
import { createWalletAdapter } from '../../../utils/wallet';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import type { ProviderConfigSectionProps } from './types';

const CLOUDFRONT_DOMAIN_REGEX =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/;

export const S3ConfigSchema = z
  .object({
    accessKeyId: z.string().min(1, 'Access Key ID is required'),
    secretAccessKey: z.string().min(1, 'Secret Access Key is required'),
    bucketName: z.string().min(1, 'Bucket Name is required'),
    region: z.string().min(1, 'Region is required'),
    pathPrefix: z.string(),
    useCloudFront: z.boolean(),
    cloudFrontDomain: z.string(),
  })
  .superRefine((values, context) => {
    if (!values.useCloudFront) return;

    if (!values.cloudFrontDomain) {
      context.addIssue({
        code: 'custom',
        path: ['cloudFrontDomain'],
        message: 'CloudFront domain is required when CloudFront is enabled',
      });
    } else if (!CLOUDFRONT_DOMAIN_REGEX.test(values.cloudFrontDomain)) {
      context.addIssue({
        code: 'custom',
        path: ['cloudFrontDomain'],
        message: 'Invalid CloudFront domain format',
      });
    }
  });

type S3FormValues = z.infer<typeof S3ConfigSchema>;
type S3TextFieldName = Exclude<keyof S3FormValues, 'useCloudFront'>;
type S3FieldErrors = Partial<Record<S3TextFieldName, string>>;

const DEFAULT_S3_FORM_VALUES: S3FormValues = {
  accessKeyId: '',
  secretAccessKey: '',
  bucketName: '',
  region: 'us-east-1',
  pathPrefix: '',
  useCloudFront: false,
  cloudFrontDomain: '',
};

function getS3FieldErrors(values: S3FormValues): S3FieldErrors {
  const result = S3ConfigSchema.safeParse(values);
  if (result.success) return {};

  const errors: S3FieldErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0] as S3TextFieldName;
    errors[field] ??= issue.message;
  }
  return errors;
}

interface S3TextFieldProps {
  name: S3TextFieldName;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled: boolean;
  error?: string;
  description?: string;
  required?: boolean;
  type?: 'text' | 'password';
}

function S3TextField({
  name,
  label,
  value,
  onChange,
  placeholder,
  disabled,
  error,
  description,
  required,
  type = 'text',
}: S3TextFieldProps) {
  const inputId = `s3-${name}`;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  return (
    <div className="space-y-2">
      <label htmlFor={inputId} className="text-neutral-300">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        id={inputId}
        name={name}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full px-4 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder={placeholder}
        disabled={disabled}
        aria-label={label}
        aria-required={required}
        aria-invalid={!!error}
        aria-describedby={
          [descriptionId, errorId].filter(Boolean).join(' ') || undefined
        }
      />
      {description && (
        <p id={descriptionId} className="text-xs text-neutral-500">
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-sm font-medium text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

export const S3ConfigSection = ({
  onConfigUpdate,
}: ProviderConfigSectionProps) => {
  const wallet = useWallet();
  const { updatePreferences } = usePreferences();
  const [showS3ConfigModal, setShowS3ConfigModal] = useState(false);
  const [s3ConfigErrors, setS3ConfigErrors] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<S3FieldErrors>({});
  const [hasValidated, setHasValidated] = useState(false);
  const [testingS3, setTestingS3] = useState(false);
  const [hasS3Configuration, setHasS3Configuration] = useState(false);
  const [currentS3Config, setCurrentS3Config] =
    useState<Partial<S3Config> | null>(null);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [formValues, setFormValues] = useState(DEFAULT_S3_FORM_VALUES);
  const [initialFormValues, setInitialFormValues] = useState(
    DEFAULT_S3_FORM_VALUES,
  );
  const isDirty =
    JSON.stringify(formValues) !== JSON.stringify(initialFormValues);

  const resetForm = (values = DEFAULT_S3_FORM_VALUES) => {
    setFormValues(values);
    setInitialFormValues(values);
    setFieldErrors({});
    setHasValidated(false);
    setS3ConfigErrors([]);
  };

  const updateFormValue = (name: S3TextFieldName, value: string) => {
    const nextValues = { ...formValues, [name]: value };
    setFormValues(nextValues);
    if (hasValidated) setFieldErrors(getS3FieldErrors(nextValues));
  };

  useEffect(() => {
    const checkS3Config = async () => {
      try {
        const configured = hasS3Config();
        setHasS3Configuration(configured);

        const publicConfig = getS3PublicConfig();
        if (publicConfig) {
          setCurrentS3Config({
            accessKeyId: '****',
            secretAccessKey: '****',
            bucketName: publicConfig.bucketName,
            region: publicConfig.region,
            pathPrefix: publicConfig.pathPrefix,
            cloudFrontDomain: publicConfig.cloudFrontDomain,
          });
        } else if (configured) {
          const walletAdapter = createWalletAdapter(wallet, {
            throwOnInvalid: false,
            includeSendTransaction: false,
          });
          if (walletAdapter) {
            const config = await decryptS3Config(walletAdapter);
            if (config) setCurrentS3Config(maskS3Config(config));
          }
        } else {
          setCurrentS3Config(null);
        }
      } catch (error) {
        captureAndReportError('Failed to check S3 configuration:', error);
      }
    };

    void checkS3Config();
  }, [wallet.publicKey, wallet.signMessage]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setHasValidated(true);

    const result = S3ConfigSchema.safeParse(formValues);
    if (!result.success) {
      setFieldErrors(getS3FieldErrors(formValues));
      return;
    }

    setTestingS3(true);
    setS3ConfigErrors([]);
    setFieldErrors({});

    const { useCloudFront, cloudFrontDomain, ...config } = result.data;
    const configToSave: S3Config = {
      ...config,
      cloudFrontDomain: useCloudFront ? cloudFrontDomain : undefined,
    };

    try {
      const provider = new S3Provider();
      const walletAdapter = createWalletAdapter(wallet, {
        throwOnInvalid: false,
        includeSendTransaction: false,
      });

      await encryptS3Config(configToSave, walletAdapter);
      await provider.initialize({ walletAdapter: walletAdapter || undefined });
      await provider.testConfiguration();

      setHasS3Configuration(true);
      setCurrentS3Config(maskS3Config(configToSave));
      setShowS3ConfigModal(false);
      resetForm();
      onConfigUpdate?.();

      try {
        await updatePreferences({ uploadProvider: 's3' });
        toast.success('S3 configured and set as your default upload provider!');
      } catch (error) {
        captureAndReportError('Failed to save S3 as default provider:', error);
        toast.error(
          'S3 configured but failed to save as default provider. Please save manually.',
        );
      }
    } catch (error) {
      captureAndReportError('S3 configuration test failed:', error);

      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        setS3ConfigErrors([
          'CORS configuration error. Please ensure your S3 bucket has proper CORS settings. ' +
            'See the documentation for detailed CORS setup instructions.',
        ]);
      } else if (
        error instanceof Error &&
        error.message.includes('AccessControlListNotSupported')
      ) {
        setS3ConfigErrors([
          'Your S3 bucket has ACLs disabled (recommended). To make objects publicly accessible, ' +
            'add a bucket policy in S3 → Permissions → Bucket Policy. See the configuration ' +
            'instructions below for the required policy.',
        ]);
      } else {
        setS3ConfigErrors([getErrorMessage(error)]);
      }
    } finally {
      setTestingS3(false);
    }
  };

  const handleClearS3Config = () => {
    if (
      confirm(
        'Are you sure you want to clear your S3 configuration? This cannot be undone.',
      )
    ) {
      clearS3Config();
      setHasS3Configuration(false);
      setCurrentS3Config(null);
      toast.success('S3 configuration cleared');
      onConfigUpdate?.();
    }
  };

  const handleDialogOpen = async () => {
    setShowS3ConfigModal(true);
    resetForm({
      ...DEFAULT_S3_FORM_VALUES,
      useCloudFront: !!currentS3Config?.cloudFrontDomain,
    });

    if (!hasS3Configuration || !currentS3Config) return;

    try {
      const walletAdapter = createWalletAdapter(wallet, {
        throwOnInvalid: false,
        includeSendTransaction: false,
      });
      if (!walletAdapter) return;

      const fullConfig = await decryptS3Config(walletAdapter);
      if (fullConfig) {
        resetForm({
          accessKeyId: fullConfig.accessKeyId,
          secretAccessKey: fullConfig.secretAccessKey,
          bucketName: fullConfig.bucketName,
          region: fullConfig.region || 'us-east-1',
          pathPrefix: fullConfig.pathPrefix || '',
          useCloudFront: !!fullConfig.cloudFrontDomain,
          cloudFrontDomain: fullConfig.cloudFrontDomain || '',
        });
      }
    } catch (error) {
      captureAndReportError('Failed to load full S3 configuration:', error);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open && isDirty && !testingS3) {
      setShowUnsavedChangesDialog(true);
      return;
    }

    setShowS3ConfigModal(open);
    if (!open) resetForm(initialFormValues);
  };

  const handleDiscardChanges = () => {
    setShowUnsavedChangesDialog(false);
    setShowS3ConfigModal(false);
    resetForm(initialFormValues);
  };

  const handleKeepEditing = () => setShowUnsavedChangesDialog(false);

  const handleUseCloudFrontChange = (checked: boolean) => {
    const nextValues = {
      ...formValues,
      useCloudFront: checked === true,
      cloudFrontDomain: checked === true ? formValues.cloudFrontDomain : '',
    };
    setFormValues(nextValues);
    if (hasValidated) setFieldErrors(getS3FieldErrors(nextValues));
  };

  return (
    <>
      <TooltipProvider>
        <div className="pt-4 mt-3 border-t border-neutral-700">
          <div className="pb-4 space-y-4">
            <h4 className="text-sm font-medium text-neutral-300 mb-3">
              Configuration
            </h4>

            {hasS3Configuration && currentS3Config ? (
              <div className="bg-neutral-900/50 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-3">
                  <div className="flex items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SecurityIcon className="size-4 fill-white cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Credentials are encrypted and stored locally</p>
                      </TooltipContent>
                    </Tooltip>
                    <span className="text-xs font-medium text-neutral-300">
                      Current Configuration
                    </span>
                    {currentS3Config.cloudFrontDomain && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <CloudIcon className="size-4 fill-teal-500 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>CloudFront CDN enabled</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>

                <div className="flex items-start justify-between">
                  <div className="space-y-1 text-xs font-mono mt-auto">
                    <p className="text-neutral-400">
                      Bucket:{' '}
                      <span className="text-neutral-300">
                        {currentS3Config.bucketName}
                      </span>
                    </p>
                    <p className="text-neutral-400">
                      Region:{' '}
                      <span className="text-neutral-300">
                        {currentS3Config.region || 'us-east-1'}
                      </span>
                    </p>
                    {currentS3Config.pathPrefix && (
                      <p className="text-neutral-400">
                        Path:{' '}
                        <span className="text-neutral-300">
                          {currentS3Config.pathPrefix}
                        </span>
                      </p>
                    )}
                    {currentS3Config.cloudFrontDomain && (
                      <p className="text-neutral-400">
                        CloudFront:{' '}
                        <span className="text-neutral-300">
                          {currentS3Config.cloudFrontDomain}
                        </span>
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={handleClearS3Config}
                      className="text-xs border border-red-600 text-red-600 px-3 py-1 rounded hover:bg-red-600/10"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={handleDialogOpen}
                      className="text-xs border border-teal-600 text-teal-600 px-3 py-1 rounded hover:bg-teal-600/10"
                    >
                      Update
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <p className="text-xs text-neutral-400 mb-4">
                  No configuration found. Set up your S3 credentials to start
                  uploading.
                </p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleDialogOpen}
                    className="border border-teal-600 text-teal-600 font-medium py-2 px-6 rounded-lg hover:bg-teal-600/10"
                  >
                    Configure S3
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </TooltipProvider>

      <Dialog open={showS3ConfigModal} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto bg-neutral-900 border-neutral-800">
          <form onSubmit={onSubmit}>
            <DialogHeader>
              <DialogTitle>Configure AWS S3</DialogTitle>
              <DialogDescription>
                Enter your AWS S3 credentials and bucket information. These
                credentials will be encrypted and stored locally in your
                browser. They are never sent to our servers.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <div className="space-y-6">
                <div className="bg-neutral-800/50 border border-neutral-700 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <WalletIcon className="size-5 fill-neutral-400 flex-shrink-0 mt-0.5 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Wallet is used for credential encryption</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-neutral-300 mb-1">
                        {wallet.publicKey
                          ? 'Wallet Connected'
                          : 'Wallet Required'}
                      </p>
                      {wallet.publicKey ? (
                        <p className="text-xs text-neutral-400">
                          Credentials will be encrypted with wallet:
                          <span className="font-mono text-neutral-300 block mt-1">
                            {wallet.publicKey.toBase58()}
                          </span>
                        </p>
                      ) : (
                        <p className="text-xs text-yellow-400">
                          Please connect your wallet to save credentials. The
                          credentials will be encrypted using your wallet's
                          public key.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-950/30 border border-yellow-800/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <InfoIcon className="size-5 fill-yellow-500 flex-shrink-0 mt-0.5 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Best practices for AWS IAM configuration</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <div className="text-sm text-yellow-300">
                      <p className="font-medium mb-1">
                        Security Recommendations:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-xs text-yellow-200">
                        <li>
                          Create a dedicated IAM user with minimal permissions
                        </li>
                        <li>
                          Only grant s3:PutObject and s3:PutObjectAcl
                          permissions
                        </li>
                        <li>Restrict access to a specific bucket</li>
                        <li>Enable bucket versioning for data protection</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {s3ConfigErrors.length > 0 && (
                  <div className="bg-red-950/30 border border-red-800/30 rounded-lg p-4">
                    <p className="text-sm font-medium text-red-400 mb-2">
                      Please fix the following errors:
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      {s3ConfigErrors.map((error, index) => (
                        <li
                          key={`${error}-${index}`}
                          className="text-xs text-red-300"
                        >
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-4">
                  <S3TextField
                    name="accessKeyId"
                    label="AWS Access Key ID"
                    value={formValues.accessKeyId}
                    onChange={(value) => updateFormValue('accessKeyId', value)}
                    placeholder="AKIAIOSFODNN7EXAMPLE"
                    disabled={testingS3}
                    error={fieldErrors.accessKeyId}
                    required
                  />

                  <S3TextField
                    name="secretAccessKey"
                    label="AWS Secret Access Key"
                    value={formValues.secretAccessKey}
                    onChange={(value) =>
                      updateFormValue('secretAccessKey', value)
                    }
                    placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                    disabled={testingS3}
                    error={fieldErrors.secretAccessKey}
                    type="password"
                    required
                  />

                  <S3TextField
                    name="bucketName"
                    label="S3 Bucket Name"
                    value={formValues.bucketName}
                    onChange={(value) => updateFormValue('bucketName', value)}
                    placeholder="my-nft-uploads"
                    disabled={testingS3}
                    error={fieldErrors.bucketName}
                    required
                  />

                  <S3TextField
                    name="region"
                    label="AWS Region"
                    value={formValues.region}
                    onChange={(value) => updateFormValue('region', value)}
                    placeholder="us-east-1"
                    disabled={testingS3}
                    error={fieldErrors.region}
                    description="Default: us-east-1. Use the region where your bucket is located."
                  />

                  <S3TextField
                    name="pathPrefix"
                    label="Path Prefix (Optional)"
                    value={formValues.pathPrefix}
                    onChange={(value) => updateFormValue('pathPrefix', value)}
                    placeholder="nft-metadata/uploads"
                    disabled={testingS3}
                    error={fieldErrors.pathPrefix}
                    description="Optional folder path within your bucket for organizing uploads."
                  />

                  <div className="border-t border-neutral-700 pt-4 mt-2">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="use-cloudfront"
                        aria-label="Use CloudFront CDN"
                        checked={formValues.useCloudFront}
                        onChange={(event) =>
                          handleUseCloudFrontChange(event.currentTarget.checked)
                        }
                        disabled={testingS3}
                        className="size-5 rounded border-neutral-600 bg-neutral-800 accent-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                      <label
                        htmlFor="use-cloudfront"
                        className="text-sm font-medium text-neutral-300 cursor-pointer"
                      >
                        Use CloudFront Distribution
                      </label>
                    </div>
                    <p className="text-xs text-neutral-400 mt-1 ml-7">
                      Enable CDN delivery through Amazon CloudFront for improved
                      global performance
                    </p>
                  </div>

                  <div
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      formValues.useCloudFront
                        ? 'max-h-[800px] opacity-100'
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="mt-4 space-y-4">
                      <S3TextField
                        name="cloudFrontDomain"
                        label="CloudFront Domain"
                        value={formValues.cloudFrontDomain}
                        onChange={(value) =>
                          updateFormValue('cloudFrontDomain', value)
                        }
                        placeholder="d1234567890.cloudfront.net or cdn.yourdomain.com"
                        disabled={testingS3}
                        error={fieldErrors.cloudFrontDomain}
                        description="Your CloudFront distribution domain for CDN delivery"
                        required
                      />

                      <div className="p-4 bg-teal-950/30 border border-teal-800/30 rounded-lg">
                        <div className="flex items-start gap-3">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InfoIcon className="size-5 fill-teal-500 flex-shrink-0 mt-0.5 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Step-by-step CloudFront setup guide</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-teal-400 mb-2">
                              🚀 CloudFront Setup Instructions
                            </p>
                            <ol className="text-xs text-teal-300 space-y-2 list-decimal list-inside">
                              <li>
                                <strong>Create Distribution:</strong> In AWS
                                Console → CloudFront → Create Distribution
                              </li>
                              <li>
                                <strong>Origin Settings:</strong>
                                <div className="ml-4 mt-1 text-neutral-300 font-mono text-xs">
                                  Origin Domain:{' '}
                                  {currentS3Config?.bucketName || 'your-bucket'}
                                  .s3.{currentS3Config?.region || 'region'}
                                  .amazonaws.com
                                </div>
                              </li>
                              <li>
                                <strong>Cache Behavior:</strong> Set Viewer
                                Protocol Policy to "Redirect HTTP to HTTPS"
                              </li>
                              <li>
                                <strong>CORS Headers:</strong> Create Response
                                Headers Policy with:
                                <div className="ml-4 mt-1 bg-neutral-950 p-2 rounded text-neutral-300 font-mono text-xs">
                                  Access-Control-Allow-Origin:
                                  https://publish.solanamobile.com
                                </div>
                              </li>
                              <li>
                                <strong>Wait for Deployment:</strong> Takes
                                15-20 minutes to deploy globally
                              </li>
                              <li>
                                <strong>Copy Domain:</strong> Use the CloudFront
                                domain (d1234567890.cloudfront.net) above
                              </li>
                            </ol>
                            <p className="text-xs text-neutral-400 mt-3">
                              <strong>Note:</strong> Ensure your S3 bucket has
                              public read access configured
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-orange-950/30 border border-orange-800/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <InfoIcon className="size-5 fill-orange-500 flex-shrink-0 mt-0.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Required for browser-based uploads</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-orange-400 mb-2">
                          ⚠️ Required S3 Bucket CORS Configuration
                        </p>
                        <p className="text-xs text-orange-300 mb-3">
                          Without proper CORS configuration, uploads will fail
                          with a "blocked by CORS policy" error.
                        </p>
                        <div className="text-xs text-neutral-300 bg-neutral-950 p-3 rounded font-mono whitespace-pre-wrap break-all">
                          {`[
  {
    "AllowedHeaders": ["*"],
    "AllowedMethods": [
      "GET", "PUT", "POST", 
      "DELETE", "HEAD"
    ],
    "AllowedOrigins": [
      "https://publish.solanamobile.com"
    ],
    "ExposeHeaders": [
      "ETag", 
      "x-amz-version-id"
    ],
    "MaxAgeSeconds": 3000
  }
]`}
                        </div>
                        <p className="text-xs text-orange-300 mt-2">
                          Add this in S3 Console → Permissions → CORS → Edit
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-950/30 border border-blue-800/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <InfoIcon className="size-5 fill-blue-500 flex-shrink-0 mt-0.5 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Required for NFT metadata accessibility</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-400 mb-2">
                          🔓 Required S3 Bucket Policy for Public Access
                        </p>
                        <p className="text-xs text-blue-300 mb-3">
                          NFT metadata must be publicly accessible. Add this
                          bucket policy to allow public read access while
                          keeping ACLs disabled (recommended).
                        </p>
                        <div className="text-xs text-neutral-300 bg-neutral-950 p-3 rounded font-mono whitespace-pre-wrap break-all">
                          {`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "PublicReadGetObject",
      "Effect": "Allow",
      "Principal": "*",
      "Action": "s3:GetObject",
      "Resource": "arn:aws:s3:::YOUR_BUCKET_NAME/*"
    }
  ]
}`}
                        </div>
                        <p className="text-xs text-blue-300 mt-2">
                          Replace YOUR_BUCKET_NAME with your actual bucket name.
                          Add this in S3 Console → Permissions → Bucket Policy →
                          Edit
                        </p>
                        <p className="text-xs text-neutral-400 mt-2">
                          Note: Keep "Block all public access" disabled for the
                          bucket, but leave "Bucket owner enforced" enabled for
                          Object Ownership.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </DialogBody>
            <DialogFooter className="gap-3">
              <button
                type="button"
                onClick={() => handleDialogClose(false)}
                disabled={testingS3}
                className="bg-neutral-700 text-white font-medium py-2 px-6 rounded-lg hover:bg-neutral-600 disabled:opacity-50 disabled:hover:bg-neutral-700 transition-all duration-150"
                style={{ cursor: testingS3 ? 'default' : 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={testingS3 || !wallet.publicKey}
                className="bg-teal-600 text-white font-medium py-2 px-6 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:hover:bg-teal-600 transition-all duration-150 relative min-w-[140px] flex items-center justify-center gap-2"
                style={{
                  cursor:
                    testingS3 || !wallet.publicKey ? 'default' : 'pointer',
                }}
              >
                {testingS3 ? (
                  <>
                    <svg
                      className="animate-spin size-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-label="Testing"
                    >
                      <title>Testing</title>
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Testing…
                  </>
                ) : !wallet.publicKey ? (
                  'Connect Wallet to Save'
                ) : (
                  'Save & Test Configuration'
                )}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showUnsavedChangesDialog}
        onOpenChange={setShowUnsavedChangesDialog}
      >
        <DialogContent className="sm:max-w-[500px] bg-neutral-900 border-neutral-800">
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes in the S3 configuration. Are you sure you
              want to discard them?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3 sm:justify-end">
            <button
              type="button"
              onClick={handleKeepEditing}
              className="bg-neutral-700 text-white font-medium py-2 px-6 rounded-lg hover:bg-neutral-600 transition-all duration-150"
            >
              Keep Editing
            </button>
            <button
              type="button"
              onClick={handleDiscardChanges}
              className="bg-red-600 text-white font-medium py-2 px-6 rounded-lg hover:bg-red-700 transition-all duration-150"
            >
              Discard Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
